import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, ChevronDown, Sparkles, ArrowRight } from 'lucide-react';
import { DUBAI_AREAS, PROPERTY_TYPES } from '../services/api';
import { WhatsAppMessages } from '../utils/whatsapp';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../contexts/CurrencyContext';
import { getPriceRangeOptions } from '../utils/currency';
import WhatsAppCTA from './WhatsAppCTA';
import ScrollTilt from './ScrollTilt';
import TextLoop from './TextLoop';
import RevealText from './RevealText';

const HeroSection = ({ onSearch }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { currency } = useCurrency();
    const [activeTab, setActiveTab] = useState('BUY');

    // Search States
    const [location, setLocation] = useState('');
    const [propertyType, setPropertyType] = useState('');
    const [priceRange, setPriceRange] = useState('');
    const [beds, setBeds] = useState('');

    // UI States
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);

    // Get dynamic price range options based on selected currency
    const priceOptions = getPriceRangeOptions(currency);

    const handleSearch = () => {
        const params = new URLSearchParams();

        if (location) params.set('district', location);
        if (propertyType) params.set('type', propertyType);
        if (priceRange) {
            const [minAED, maxAED] = priceRange.split('-');
            if (minAED) params.set('minPrice', minAED);
            if (maxAED) params.set('maxPrice', maxAED);
        }
        if (beds) params.set('beds', beds);

        if (activeTab === 'OFF-PLAN') {
            params.set('category', 'off-plan');
        }

        const queryString = params.toString();
        navigate(`/projects${queryString ? '?' + queryString : ''}`);
    };

    const handleLocationSelect = (area) => {
        setLocation(area);
        setShowLocationDropdown(false);
    };

    const filteredAreas = DUBAI_AREAS.filter(area =>
        area.toLowerCase().includes(location.toLowerCase())
    );

    const tabs = [
        { id: 'BUY', label: t('hero.tabs.buy') },
        { id: 'OFF-PLAN', label: t('hero.tabs.off_plan') }
    ];

    return (
        <>
            <style>{`
                .hero-section {
                    position: relative;
                    min-height: 100vh;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    /* hero-bg.png was 19 MB — a single 7000px PNG. These are
                       the same image re-encoded: 147 KB / 82 KB / 36 KB. The
                       matching variant is preloaded in index.html. */
                    background-image: url("/hero-bg-1920.webp");
                    background-size: cover;
                    background-position: center top;
                    background-repeat: no-repeat;
                    padding: 100px 16px 40px;
                }

                @media (min-width: 769px) and (max-width: 1400px) {
                    .hero-section {
                        background-image: url("/hero-bg-1280.webp");
                    }
                }

                @media (max-width: 768px) {
                    .hero-section {
                        background-image: url("/hero-bg-768.webp");
                        background-position: 50% 10%;
                        min-height: auto;
                        padding: 90px 12px 30px;
                    }

                    .hero-description {
                        font-size: 0.85rem !important;
                        margin-bottom: 1.25rem !important;
                    }

                    .search-container {
                        padding: 0.75rem !important;
                    }

                    .search-tabs-row {
                        margin-bottom: 0.75rem !important;
                        padding-bottom: 0.5rem !important;
                    }

                    .search-input, .search-select {
                        height: 42px !important;
                        font-size: 0.85rem !important;
                    }

                    .search-btn {
                        height: 42px !important;
                    }

                    .hero-main-title {
                        font-size: 1.6rem !important;
                    }
                }

                @media (max-width: 480px) {
                    .hero-section {
                        padding: 80px 10px 20px;
                    }

                    .hero-main-title {
                        font-size: 1.4rem !important;
                    }

                    .search-inputs-grid {
                        gap: 0.5rem !important;
                    }
                }

                .hero-overlay {
                    position: absolute;
                    inset: 0;
                    /* Two layers: a soft pool of shade behind the centred text
                       block, over a bottom-weighted gradient. The photo still
                       reads at the edges, but the headline never sits on the
                       bright building lights. */
                    background:
                        radial-gradient(ellipse 85% 55% at 50% 42%,
                            rgba(5, 5, 16, 0.78) 0%,
                            rgba(5, 5, 16, 0.45) 55%,
                            rgba(5, 5, 16, 0) 100%),
                        linear-gradient(to top,
                            rgba(5, 5, 16, 0.92) 0%,
                            rgba(5, 5, 16, 0.5) 35%,
                            rgba(5, 5, 16, 0.22) 65%,
                            rgba(5, 5, 16, 0.15) 100%);
                    z-index: 1;
                }

                /* The background photo used to stop dead where the section
                   ended. This carries it down into the page colour so there is
                   no visible line between the hero and what follows. */
                .hero-section::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    height: 34%;
                    background: linear-gradient(180deg,
                        rgba(5, 5, 16, 0) 0%,
                        rgba(5, 5, 16, 0.55) 45%,
                        var(--color-dark-bg) 100%);
                    pointer-events: none;
                    z-index: 2;
                }

                .hero-content {
                    position: relative;
                    z-index: 10;
                    text-align: center;
                    width: 100%;
                    max-width: 1200px;
                }

                /* ── First-impression sequence ──────────────────────────────
                   Each piece rises into place in reading order. The
                   'backwards' fill mode holds the start frame during the
                   delay so nothing flashes in at full opacity before its
                   turn. The reduced-motion block in index.css collapses all
                   of this to ~0ms. */
                .hero-rise {
                    /* Fill mode MUST be 'both': 'backwards' holds the start
                       frame during the delay, 'forwards' holds the end frame
                       after it finishes. Setting only one made each element
                       animate in and then snap back to opacity 0.
                       No base opacity:0 here — if the animation never runs,
                       the text must still be visible. */
                    animation: hero-rise 0.7s cubic-bezier(0.16, 1, 0.3, 1)
                               var(--rise-delay, 0s) both;
                }

                @keyframes hero-rise {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* The search box also fades its glass in, so it settles rather
                   than appearing as a hard panel. */
                .scroll-tilt.hero-rise {
                    animation-name: hero-rise-glass;
                }

                /* ── Scroll-linked 3D tilt stage ────────────────────────────
                   Perspective on the outer element, transform on the inner
                   one, so the entrance animation and the scroll tilt never
                   fight over the same transform property. ScrollTilt writes
                   only the three custom properties; styling stays here. */
                .scroll-tilt {
                    perspective: 1400px;
                    perspective-origin: 50% 0%;
                    width: 100%;
                }

                .scroll-tilt-inner {
                    transform:
                        rotateX(var(--tilt, 0deg))
                        scale(var(--tilt-scale, 1))
                        translateY(var(--tilt-lift, 0px));
                    transform-origin: 50% 0%;
                    will-change: transform;
                }

                /* ── Word-by-word reveal for the sentence under the title ──
                   No base opacity: the 'both' fill holds the start frame during
                   each word's delay, so if the animation never runs the text is
                   still on screen rather than invisible. */
                .reveal-word {
                    display: inline-block;
                    animation: reveal-word 0.62s cubic-bezier(0.16, 1, 0.3, 1)
                               calc(var(--reveal-base, 0s) + var(--i, 0) * var(--reveal-step, 0.04s))
                               both;
                }

                @keyframes reveal-word {
                    from { opacity: 0; transform: translateY(9px); filter: blur(5px); }
                    to   { opacity: 1; transform: translateY(0);   filter: blur(0); }
                }

                @keyframes hero-rise-glass {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0) scale(1); }
                }

                .hero-main-title {
                    font-size: clamp(1.9rem, 5.2vw, 4.15rem);
                    font-weight: 900;
                    color: #fff;
                    margin-bottom: 0.65rem;
                    /* Tight dark halo + a wider ambient one: keeps the edges
                       crisp against bright pixels without looking embossed. */
                    text-shadow:
                        0 1px 2px rgba(0, 0, 0, 0.55),
                        0 3px 20px rgba(0, 0, 0, 0.5);
                    line-height: 1.14;
                    letter-spacing: -0.015em;
                }

                [dir="rtl"] .hero-main-title {
                    font-weight: 700;
                    line-height: 1.5;
                    letter-spacing: normal;
                }

                /* ── Rotating headline word ─────────────────────────────
                   Gold, not the original's violet: gold is the locked brand
                   colour and this line already carried it. */
                .text-loop {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.25em;
                    margin-top: 0.45rem;
                }

                .text-loop-static { white-space: nowrap; }

                .text-loop-track {
                    display: inline-flex;
                    align-items: center;
                }

                .text-loop-clip {
                    display: inline-block;
                    overflow: hidden;
                    white-space: nowrap;
                    vertical-align: bottom;
                    transition-property: width, opacity;
                    transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
                }

                .text-loop-word {
                    display: inline-block;
                    white-space: nowrap;
                    padding-inline-end: 0.06em;
                    color: transparent;
                    background-image: linear-gradient(100deg,
                        #F5C838 0%, #FFE9A3 42%, #FFD24A 72%, #F0BE2E 100%);
                    background-clip: text;
                    -webkit-background-clip: text;
                    filter: drop-shadow(0 0 26px rgba(255, 200, 60, 0.42));
                }

                .text-loop-cursor {
                    display: inline-block;
                    width: 3px;
                    height: 0.95em;
                    margin-inline-start: 0.1em;
                    background: var(--color-gold);
                    border-radius: 2px;
                    box-shadow: 0 0 12px rgba(255, 215, 0, 0.6);
                    animation: text-loop-blink 1.05s steps(1, end) infinite;
                }

                @keyframes text-loop-blink {
                    0%, 45%   { opacity: 1; }
                    50%, 100% { opacity: 0.15; }
                }

                @media (min-width: 768px) {
                    .text-loop-cursor { width: 4px; }
                }

                /* ── Hairline under the title ───────────────────────────────
                   Fades out at both ends so it reads as a rule of light
                   rather than a drawn line. */
                .hero-divider {
                    width: clamp(70px, 12vw, 116px);
                    height: 1px;
                    margin: 1.15rem auto 1.35rem;
                    background: linear-gradient(90deg,
                        transparent 0%,
                        rgba(255, 215, 0, 0.75) 50%,
                        transparent 100%);
                }

                .hero-description {
                    font-size: clamp(0.95rem, 2vw, 1.25rem);
                    color: #dcdce6;
                    font-weight: 500;
                    /* Narrow measure: the line is short now, so this keeps it
                       stacked under the title as two lines instead of one wide
                       band. Arabic and Russian run longer than English at the
                       same character count, hence the ch-based ceiling. */
                    max-width: min(46ch, 470px);
                    margin: 0 auto 2rem;
                    line-height: 1.65;
                    text-shadow: 0 1px 12px rgba(0, 0, 0, 0.55);
                }

                [dir="rtl"] .hero-description {
                    font-weight: 500;
                    line-height: 1.95;
                }

                .search-container {
                    max-width: 1000px;
                    margin: 0 auto;
                    /* Slimmer, glassy — background now bleeds through */
                    padding: clamp(0.75rem, 2vw, 1.25rem);
                    background: rgba(10, 12, 24, 0.42);
                    /* A quiet neutral edge. The gold border plus two inset
                       glows made the panel compete with the headline. */
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 16px;
                    backdrop-filter: blur(20px) saturate(1.2);
                    -webkit-backdrop-filter: blur(20px) saturate(1.2);
                    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
                    overflow: visible !important;
                }

                .search-tabs-row {
                    display: flex;
                    gap: clamp(1rem, 3vw, 2rem);
                    margin-bottom: 1rem;
                    border-bottom: 1px solid rgba(255,255,255,0.08);
                    padding-bottom: 0.75rem;
                    justify-content: center;
                }

                .search-tab-btn {
                    background: none;
                    border: none;
                    color: #fff;
                    font-size: clamp(0.85rem, 2vw, 1rem);
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    padding-bottom: 5px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }

                .search-tab-btn.active {
                    color: var(--color-gold);
                    border-bottom: 2px solid var(--color-gold);
                }

                .search-inputs-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1rem;
                }

                @media (min-width: 768px) {
                    .search-inputs-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (min-width: 1024px) {
                    .search-inputs-grid {
                        grid-template-columns: 1.5fr 1fr 1fr 1fr 120px;
                    }
                }

                .search-input-wrapper {
                    position: relative;
                }

                .search-input, .search-select {
                    width: 100%;
                    background: rgba(0, 0, 0, 0.35);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    padding: 12px 12px 12px 40px;
                    border-radius: 10px;
                    color: #fff;
                    outline: none;
                    font-size: 0.9rem;
                    height: 46px;
                    transition: border-color 0.2s, background 0.2s;
                }

                .search-input:focus, .search-select:focus {
                    border-color: rgba(255, 215, 0, 0.5);
                    background: rgba(0, 0, 0, 0.5);
                }

                .search-input::placeholder {
                    color: rgba(255, 255, 255, 0.5);
                }
                
                .search-select {
                    padding-left: 12px;
                }

                .search-select option {
                    background: #1a1a2e;
                    color: #fff;
                }

                .location-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: rgba(20, 20, 35, 0.98);
                    border: 1px solid rgba(255,215,0,0.2);
                    border-radius: 8px;
                    margin-top: 4px;
                    max-height: 350px;
                    overflow-y: auto;
                    z-index: 1000;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
                }

                .location-item {
                    padding: 10px 15px;
                    cursor: pointer;
                    color: #fff;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    transition: background 0.2s;
                    display: flex;
                    align-items: center;
                }

                .location-item:hover {
                    background: rgba(255,215,0,0.1);
                }

                .search-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    width: 100%;
                    height: 48px;
                    background: var(--color-gold);
                    color: #000;
                    font-weight: 700;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: transform 0.2s;
                }
                
                .search-btn:hover {
                    transform: translateY(-2px);
                }

                .hero-ai-cta {
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                    margin-top: 1.25rem;
                    padding: 10px 4px;
                    /* Was a gold/purple gradient pill — a third heavy element
                       under a headline and a search panel. Now a plain link, so
                       the search box is unambiguously the primary action. */
                    color: rgba(255, 255, 255, 0.9);
                    font-size: clamp(0.85rem, 2vw, 0.98rem);
                    font-weight: 600;
                    text-decoration: none;
                    transition: color 0.25s ease;
                }

                .hero-ai-cta:hover {
                    color: var(--color-gold);
                }

                .hero-ai-cta .hero-ai-spark { color: var(--color-gold); flex-shrink: 0; }

                .hero-ai-cta .hero-ai-arrow {
                    color: var(--color-gold);
                    flex-shrink: 0;
                    transition: transform 0.25s ease;
                }

                .hero-ai-cta:hover .hero-ai-arrow { transform: translateX(4px); }

                [dir="rtl"] .hero-ai-cta .hero-ai-arrow { transform: scaleX(-1); }
                [dir="rtl"] .hero-ai-cta:hover .hero-ai-arrow { transform: scaleX(-1) translateX(4px); }

                .currency-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    background: rgba(255, 215, 0, 0.1);
                    color: var(--color-gold);
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    margin-left: 6px;
                }
            `}</style>

            <div className="hero-section">
                <div className="hero-overlay"></div>

                <div className="hero-content">
                    <h1 className="hero-main-title hero-rise" style={{ '--rise-delay': '0.04s' }}>
                        {t('hero.dubai_most')}
                        <TextLoop
                            staticText=""
                            rotatingTexts={t('hero.rotating', { returnObjects: true })}
                            interval={3200}
                        />
                    </h1>

                    <div className="hero-divider hero-rise" style={{ '--rise-delay': '0.1s' }} />

                    <p className="hero-description">
                        <RevealText text={t('hero.description')} delay={0.18} step={0.038} />
                    </p>

                    {/* Search Box — lies back at rest, flattens as you scroll */}
                    <ScrollTilt className="hero-rise" style={{ '--rise-delay': '0.22s' }}>
                    <div className="glass-card search-container">
                        {/* Tabs */}
                        <div className="search-tabs-row">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`search-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Inputs Row */}
                        <div className="search-inputs-grid">

                            {/* Location (District) */}
                            <div className="search-input-wrapper">
                                <MapPin size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#888' }} />
                                <input
                                    type="text"
                                    placeholder={t('hero.search.placeholder') || "Location / District"}
                                    value={location}
                                    onChange={(e) => {
                                        setLocation(e.target.value);
                                        setShowLocationDropdown(true);
                                    }}
                                    onFocus={() => setShowLocationDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowLocationDropdown(false), 200)}
                                    className="search-input"
                                />
                                {showLocationDropdown && filteredAreas.length > 0 && (
                                    <div className="location-dropdown">
                                        {filteredAreas.map((area) => (
                                            <div
                                                key={area}
                                                onMouseDown={() => handleLocationSelect(area)}
                                                className="location-item"
                                            >
                                                <MapPin size={14} style={{ marginRight: '8px', color: 'var(--color-neon-blue)' }} />
                                                {area}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Type Select */}
                            <select
                                value={propertyType}
                                onChange={(e) => setPropertyType(e.target.value)}
                                className="search-select"
                                aria-label={t('hero.search.type')}
                            >
                                <option value="">{t('hero.search.type')}</option>
                                {PROPERTY_TYPES.map((pt) => (
                                    <option key={pt.value} value={pt.value}>
                                        {t(pt.labelKey) || pt.value}
                                    </option>
                                ))}
                            </select>

                            {/* Beds Select */}
                            <select
                                value={beds}
                                onChange={(e) => setBeds(e.target.value)}
                                className="search-select"
                                aria-label={t('hero.search.beds') || 'Bedrooms'}
                            >
                                <option value="">{t('hero.search.beds') || 'Bedrooms'}</option>
                                <option value="studio">{t('hero.search.studio')}</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5+</option>
                            </select>

                            {/* Price Range */}
                            <select
                                value={priceRange}
                                onChange={(e) => setPriceRange(e.target.value)}
                                className="search-select"
                                aria-label={t('hero.search.price') || 'Price'}
                            >
                                <option value="">{t('hero.search.price') || 'Price'}</option>
                                {priceOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>

                            {/* Search Button */}
                            <button
                                className="btn-primary search-btn"
                                onClick={handleSearch}
                            >
                                <Search size={18} />
                                {t('hero.search.find')}
                            </button>
                        </div>
                    </div>
                    </ScrollTilt>

                    {/* General WhatsApp Inquiry */}
                    {/* AI advisor — conversational alternative to the filters above */}
                    <Link to="/ai" className="hero-ai-cta hero-rise" style={{ '--rise-delay': '0.31s' }}>
                        <Sparkles size={18} className="hero-ai-spark" aria-hidden="true" />
                        {t('ai.hero_cta')}
                        <ArrowRight size={17} className="hero-ai-arrow" aria-hidden="true" />
                    </Link>

                    <div className="hero-rise" style={{ marginTop: '1.5rem', '--rise-delay': '0.38s' }}>
                        <WhatsAppCTA
                            message={WhatsAppMessages.generalInquiry || 'Hello, I need help finding a property.'}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default HeroSection;
